import { useState } from "react";
import { Loader2 } from "lucide-react";
import { api, errText } from "../../lib/api";
import { Drawer } from "./ui";

const DAYS = [["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"], ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"]];
const DEFAULT_HOURS = Object.fromEntries(DAYS.map(([d]) => [d, { open: "09:00", close: "19:00", closed: d === "sun" }]));

const Toggle = ({ label, hint, checked, onChange, testid }) => (
  <label className="flex items-center justify-between gap-3 border border-brass-light p-2.5 rounded-sm cursor-pointer">
    <span>
      <span className="text-sm text-ink block">{label}</span>
      {hint ? <span className="text-[11px] text-muted">{hint}</span> : null}
    </span>
    <input type="checkbox" className="w-4 h-4 accent-[#B4884A]" checked={!!checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />
  </label>
);

// Create / edit an admin-managed partner florist.
export default function PartnerEditor({ mode, partner, cities, onClose, onSaved }) {
  const [f, setF] = useState(() => partner ? {
    ...partner, working_hours: { ...DEFAULT_HOURS, ...(partner.working_hours || {}) }, login_password: "",
  } : {
    shop_name: "", city_id: cities[0]?.id || "", owner_name: "", whatsapp: "", phone: "", sms_phone: "", email: "", address: "",
    working_hours: DEFAULT_HOURS, notify_channels: { whatsapp: true, email: true, sms: false },
    login_email: "", login_password: "", is_primary: false, delivery_mode: "network", serves_cities: [],
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const set = (patch) => setF((x) => ({ ...x, ...patch }));
  const setDay = (d, patch) => set({ working_hours: { ...f.working_hours, [d]: { ...f.working_hours[d], ...patch } } });

  const save = async () => {
    setBusy(true); setMsg("");
    try {
      if (mode === "create") {
        const body = { ...f, login_email: f.login_email || f.email };
        await api.post("/admin/partners", body);
        onSaved?.(`${f.shop_name} added${f.is_primary ? " as primary" : ""}.`);
      } else {
        const body = {
          shop_name: f.shop_name, owner_name: f.owner_name, whatsapp: f.whatsapp, phone: f.phone, sms_phone: f.sms_phone,
          email: f.email || undefined, address: f.address, working_hours: f.working_hours, notify_channels: f.notify_channels,
          delivery_mode: f.delivery_mode, serves_cities: f.serves_cities,
          ...(f.login_password ? { login_password: f.login_password } : {}),
        };
        await api.put(`/admin/partners/${partner.id}`, body);
        onSaved?.(`${f.shop_name} updated.`);
      }
    } catch (e) { setMsg(errText(e)); }
    finally { setBusy(false); }
  };

  return (
    <Drawer testid="partner-editor" title={mode === "create" ? "Add partner florist" : `Edit ${partner.shop_name}`}
            subtitle="Fleurinelle manages the catalogue and prices; the partner only prepares and delivers." onClose={onClose}>
      <div className="p-6 space-y-5">
        <section className="space-y-2">
          <p className="smallcaps">Shop</p>
          <div className="grid sm:grid-cols-2 gap-2">
            <input className="field" placeholder="Shop name" data-testid="partner-shop-name" value={f.shop_name} onChange={(e) => set({ shop_name: e.target.value })} />
            <select className="field" data-testid="partner-city" value={f.city_id} disabled={mode === "edit"} onChange={(e) => set({ city_id: e.target.value })}>
              {cities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <input className="field" placeholder="Contact person" data-testid="partner-owner" value={f.owner_name} onChange={(e) => set({ owner_name: e.target.value })} />
            <input className="field" placeholder="Shop address" data-testid="partner-address" value={f.address} onChange={(e) => set({ address: e.target.value })} />
          </div>
          <select className="field" data-testid="partner-delivery-mode" value={f.delivery_mode} onChange={(e) => set({ delivery_mode: e.target.value })}>
            <option value="network">Deliveries by the Fleurinelle driver network</option>
            <option value="self">Partner delivers with its own team</option>
          </select>
        </section>

        <section className="space-y-2">
          <p className="smallcaps">Contacts & alert channels</p>
          <div className="grid sm:grid-cols-3 gap-2">
            <input className="field" placeholder="WhatsApp number" data-testid="partner-whatsapp" value={f.whatsapp} onChange={(e) => set({ whatsapp: e.target.value })} />
            <input className="field" placeholder="Contact e-mail" data-testid="partner-email" value={f.email} onChange={(e) => set({ email: e.target.value })} />
            <input className="field" placeholder="SMS number" data-testid="partner-sms-phone" value={f.sms_phone} onChange={(e) => set({ sms_phone: e.target.value })} />
          </div>
          <div className="grid sm:grid-cols-3 gap-2">
            <Toggle label="WhatsApp" hint="New-order & hand-over alerts" checked={f.notify_channels.whatsapp} testid="partner-channel-whatsapp"
                    onChange={(v) => set({ notify_channels: { ...f.notify_channels, whatsapp: v } })} />
            <Toggle label="Email" hint="With the one-tap Accept button" checked={f.notify_channels.email} testid="partner-channel-email"
                    onChange={(v) => set({ notify_channels: { ...f.notify_channels, email: v } })} />
            <Toggle label="SMS" hint="Short text with the Accept link" checked={f.notify_channels.sms} testid="partner-channel-sms"
                    onChange={(v) => set({ notify_channels: { ...f.notify_channels, sms: v } })} />
          </div>
        </section>

        <section className="space-y-2">
          <p className="smallcaps">Working hours <span className="normal-case text-muted">(Africa/Casablanca — the 1-hour acceptance clock only runs during these hours)</span></p>
          <div className="space-y-1" data-testid="partner-hours">
            {DAYS.map(([d, label]) => {
              const h = f.working_hours[d];
              return (
                <div key={d} className="flex items-center gap-2 text-sm">
                  <span className="w-10 text-muted">{label}</span>
                  <input type="time" className="field py-1 w-28" value={h.open} disabled={h.closed} data-testid={`partner-hours-${d}-open`} onChange={(e) => setDay(d, { open: e.target.value })} />
                  <span className="text-muted">–</span>
                  <input type="time" className="field py-1 w-28" value={h.close} disabled={h.closed} data-testid={`partner-hours-${d}-close`} onChange={(e) => setDay(d, { close: e.target.value })} />
                  <label className="text-xs text-muted inline-flex items-center gap-1 ms-2">
                    <input type="checkbox" checked={h.closed} data-testid={`partner-hours-${d}-closed`} onChange={(e) => setDay(d, { closed: e.target.checked })} /> closed
                  </label>
                </div>
              );
            })}
          </div>
        </section>

        <section className="space-y-2">
          <p className="smallcaps">Also serves</p>
          <div className="flex flex-wrap gap-1" data-testid="partner-serves-cities">
            {cities.filter((c) => c.id !== f.city_id).map((c) => {
              const on = f.serves_cities.includes(c.id);
              return (
                <button key={c.id} type="button" data-testid={`partner-serves-${c.id}`}
                        className={`text-[11px] border px-2 py-0.5 ${on ? "bg-ink text-plaster border-ink" : "border-brass text-muted"}`}
                        onClick={() => set({ serves_cities: on ? f.serves_cities.filter((x) => x !== c.id) : [...f.serves_cities, c.id] })}>{c.name}</button>
              );
            })}
          </div>
        </section>

        <section className="space-y-2">
          <p className="smallcaps">Florist app login</p>
          <div className="grid sm:grid-cols-2 gap-2">
            {mode === "create" ? (
              <input className="field" placeholder="Login e-mail (defaults to contact e-mail)" data-testid="partner-login-email" value={f.login_email} onChange={(e) => set({ login_email: e.target.value })} />
            ) : <input className="field" value={partner.login_email || "—"} disabled data-testid="partner-login-email" />}
            <input className="field" type="password" placeholder={mode === "create" ? "Password (8+ characters)" : "New password (leave blank to keep)"}
                   data-testid="partner-login-password" value={f.login_password} onChange={(e) => set({ login_password: e.target.value })} />
          </div>
          {mode === "create" ? (
            <Toggle label="Make this the primary florist of the city" hint="Receives every new order in the city first" checked={f.is_primary}
                    testid="partner-is-primary" onChange={(v) => set({ is_primary: v })} />
          ) : null}
        </section>

        {msg ? <p className="text-sm text-danger" data-testid="partner-editor-message">{msg}</p> : null}
        <div className="flex gap-2 pt-2 sticky bottom-0 bg-plaster py-3 -mx-6 px-6 border-t border-brass-light">
          <button className="btn-primary text-xs px-4 inline-flex items-center gap-2" data-testid="partner-editor-save" disabled={busy} onClick={save}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : null}{mode === "create" ? "Create partner" : "Save changes"}
          </button>
          <button className="btn-ghost text-xs px-4" onClick={onClose} data-testid="partner-editor-cancel">Cancel</button>
        </div>
      </div>
    </Drawer>
  );
}
